import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useInvoices, type InvoiceFilterStatus } from "@/hooks/useInvoices";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { handleRpcError } from "@/lib/rpcErrors";
import { StatusBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { exportPdf } from "@/lib/export";
import { InvoicePrintTemplate } from "@/components/shared/print-templates/InvoicePrintTemplate";
import { Printer } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { type ColumnDef } from "@tanstack/react-table";

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export const InvoiceTable = () => {
  const isMobile = useIsMobile();
  const [statusFilter, setStatusFilter] = useState<InvoiceFilterStatus>("draft");
  const pagination = usePagination();
  const { data: rows, isLoading } = useInvoices(statusFilter, pagination);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);

  const [paymentDialog, setPaymentDialog] = useState<{ id: string; total: number; amountPaid: number } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["approval_counts"] });
    qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    qc.invalidateQueries({ predicate: (q) => ["project_billed_to_date", "project_collected_to_date", "project_contract_value"].includes(q.queryKey[0] as string) });
  };

  const approve = async (id: string) => {
    setBusy(id);
    try {
      const { error } = await supabase.rpc("approve_invoice", { p_invoice_id: id });
      if (error) throw error;
      toast.success("Invoice approved");
      invalidateAll();
    } catch (err) { handleRpcError(err); } finally { setBusy(null); }
  };

  const markSent = async (id: string) => {
    setBusy(id);
    try {
      const { error } = await supabase.rpc("transition_invoice_status", { p_invoice_id: id, p_new_status: "sent" });
      if (error) throw error;
      toast.success("Invoice marked as sent");
      invalidateAll();
    } catch (err: any) { handleRpcError(err); } finally { setBusy(null); }
  };

  const recordPayment = async () => {
    if (!paymentDialog) return;
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(paymentDialog.id);
    try {
      const { data, error } = await supabase.rpc("transition_invoice_status", {
        p_invoice_id: paymentDialog.id, p_new_status: "paid",
        p_payment_amount: amount, p_payment_ref: paymentRef || null, p_payment_method: paymentMethod || null,
      });
      if (error) throw error;
      const newStatus = (data as any)?.new_status;
      toast.success(newStatus === "paid" ? "Invoice paid in full" : "Partial payment recorded");
      invalidateAll();
      setPaymentDialog(null); setPaymentAmount(""); setPaymentRef(""); setPaymentMethod("");
    } catch (err: any) { handleRpcError(err); } finally { setBusy(null); }
  };

  const printInvoice = async (inv: any) => {
    const { data: lines } = await supabase.from("invoice_lines").select("description, quantity, unit_price, line_total").eq("invoice_id", inv.id);
    exportPdf(
      <InvoicePrintTemplate data={{
        invoice_number: inv.invoice_number, invoice_date: inv.invoice_date, due_date: inv.due_date,
        status: inv.status, total: inv.total, amount_paid: inv.amount_paid || 0,
        payment_method: inv.payment_method, payment_reference: inv.payment_reference,
        customer_name: (inv.projects as any)?.customers?.name, project_name: (inv.projects as any)?.name,
        notes: inv.notes,
        line_items: (lines ?? []).map((l: any) => ({ description: l.description, quantity: l.quantity, unit_price: l.unit_price, line_total: l.line_total })),
      }} />,
      `Invoice ${inv.invoice_number}`
    );
  };

  const renderAction = (inv: any) => {
    if (inv.status === "draft") return <Button size="sm" variant="default" className="h-7 text-xs" disabled={busy === inv.id} onClick={(e) => { e.stopPropagation(); approve(inv.id); }}>Approve</Button>;
    if (inv.status === "approved") return <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === inv.id} onClick={(e) => { e.stopPropagation(); markSent(inv.id); }}>Mark Sent</Button>;
    if (["sent", "partially_paid", "overdue"].includes(inv.status)) return (
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === inv.id}
        onClick={(e) => { e.stopPropagation(); setPaymentDialog({ id: inv.id, total: inv.total, amountPaid: inv.amount_paid || 0 }); }}>
        Record Payment
      </Button>
    );
    return null;
  };

  const columns = useMemo<ColumnDef<any, any>[]>(() => [
    { accessorKey: "invoice_number", header: "Invoice #", cell: ({ getValue }) => <span className="font-medium">{getValue()}</span> },
    { id: "project", header: "Project", accessorFn: (row: any) => (row.projects as any)?.name ?? "—" },
    { accessorKey: "invoice_date", header: "Date", cell: ({ getValue }) => format(new Date(getValue()), "MMM d, yyyy") },
    { accessorKey: "total", header: "Total", cell: ({ getValue }) => <span className="text-right block">{fmtCurrency(getValue())}</span> },
    { accessorKey: "amount_paid", header: "Paid", cell: ({ getValue }) => <span className="text-right block">{getValue() > 0 ? fmtCurrency(getValue()) : "—"}</span> },
    { accessorKey: "status", header: "Status", cell: ({ getValue }) => <StatusBadge status={getValue()} /> },
    {
      id: "actions", header: "Actions", enableSorting: false, enableColumnFilter: false,
      cell: ({ row }) => {
        const inv = row.original;
        return (
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Print Invoice" onClick={() => printInvoice(inv)}>
              <Printer className="h-3.5 w-3.5" />
            </Button>
            {renderAction(inv)}
          </div>
        );
      },
    },
  ], [busy]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Status:</span>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as InvoiceFilterStatus); pagination.setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>
        {rows && rows.length > 0 && <ExportToolbar entityType="invoices" data={rows} filterDescription={`status: ${statusFilter}`} />}
      </div>

      {isMobile ? (
        <>
          {isLoading ? <p className="p-4 text-muted-foreground">Loading…</p> : !rows?.length ? <p className="p-4 text-muted-foreground">No invoices found.</p> : (
            <div className="space-y-3">
              {rows.map((inv: any) => (
                <Card key={inv.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{inv.invoice_number}</span>
                          <StatusBadge status={inv.status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">{(inv.projects as any)?.name ?? "—"}</p>
                        <p className="text-xs text-muted-foreground">{format(new Date(inv.invoice_date), "MMM d, yyyy")}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-lg font-bold">{fmtCurrency(inv.total)}</p>
                        {inv.amount_paid > 0 && <p className="text-xs text-muted-foreground">Paid: {fmtCurrency(inv.amount_paid)}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => printInvoice(inv)}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      {renderAction(inv)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <PaginationControls pagination={pagination} />
        </>
      ) : (
        <>
          <DataTable
            tableId="invoices-list"
            columns={columns}
            data={rows ?? []}
            isLoading={isLoading}
            emptyMessage="No invoices found."
          />
          <PaginationControls pagination={pagination} />
        </>
      )}

      <Dialog open={!!paymentDialog} onOpenChange={(o) => { if (!o) setPaymentDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>{paymentDialog && `Remaining: ${fmtCurrency(paymentDialog.total - paymentDialog.amountPaid)}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Amount</Label><Input type="number" placeholder="0.00" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} /></div>
            <div className="space-y-1">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem><SelectItem value="ach">ACH</SelectItem>
                  <SelectItem value="wire">Wire</SelectItem><SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Reference (optional)</Label><Input placeholder="Check #, ACH ref, etc." value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(null)}>Cancel</Button>
            <Button disabled={busy !== null} onClick={recordPayment}>{busy ? "Saving…" : "Record"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
